import React, { useState, useMemo, useRef } from 'react';
import { CenteredModal } from '~/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faUserShield, faExclamationTriangle, faInfoCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import UserRoleAPI from '~/api/userRoleApi';
import RolePermissionAPI from '~/api/rolePermissionApi';
import Select from 'react-select';
import databaseApi from '~/api/databaseApi';
import { useEffect } from 'react';
import PermissionTab from './PermissionTab';
import '~/pages/authentications/auth.css';

function CreateAdminModal({ show, onClose }) {
    const [formData, setFormData] = useState({
        UserName: '',
        Email: '',
        Password: '',
        ConfirmPassword: '',
        SuperAdminPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const timeoutRef = useRef(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showSuperPassword, setShowSuperPassword] = useState(false);
    const [databases, setDatabases] = useState([]);
    const [selectedDb, setSelectedDb] = useState(null);

    const [availableRoles, setAvailableRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState(['Admin']);
    const [showSuperAdminWarning, setShowSuperAdminWarning] = useState(false);

    const [activeTab, setActiveTab] = useState('account');
    const [allPermissions, setAllPermissions] = useState([]);
    const [rolePermissions, setRolePermissions] = useState([]);
    const [showPermissionsConfirm, setShowPermissionsConfirm] = useState(false);

    useEffect(() => {
        if (show) {
            RolePermissionAPI.getRoles().then(res => {
                if (res.data?.Succeeded) {
                    const roles = res.data.Data || [];
                    setAvailableRoles(roles.filter(r => r.Name !== 'User'));
                    setSelectedRoles(['Admin']);
                }
            }).catch(err => console.log(err));

            RolePermissionAPI.getAllPermissions().then(res => {
                if (res.data?.Succeeded) {
                    setAllPermissions(res.data.Data || []);
                }
            }).catch(err => console.log(err));

            databaseApi.getAllDatabases().then(res => {
                if (res.data?.Succeeded || res.data?.succeeded || Array.isArray(res.data)) {
                    const dbList = Array.isArray(res.data) ? res.data : (res.data?.Data ?? res.data?.data ?? []);
                    setDatabases([{ value: '', label: 'Current Database (Default)' }, ...dbList.map(db => ({ 
                        value: db.DatabaseName || db, 
                        label: db.DatabaseName || db 
                    }))]);
                    if (!selectedDb) {
                        setSelectedDb({ value: '', label: 'Current Database (Default)' });
                    }
                }
            }).catch(err => console.log(err));
        }
    }, [show]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field when typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const requirements = useMemo(() => [
        { label: "6+ characters", met: formData.Password.length >= 6 },
        { label: "Uppercase", met: /[A-Z]/.test(formData.Password) },
        { label: "Lowercase", met: /[a-z]/.test(formData.Password) },
        { label: "Number", met: /\d/.test(formData.Password) },
        { label: "Special char", met: /[@$!%*?&]/.test(formData.Password) },
    ], [formData.Password]);

    const strength = useMemo(() => {
        const metCount = requirements.filter(r => r.met).length;
        if (metCount === 0) return { percent: 0, color: "#e5e7eb", text: "", textColor: "#9ca3af" };
        if (metCount <= 2) return { percent: 30, color: "#ef4444", text: "Weak", textColor: "#ef4444" };
        if (metCount <= 3) return { percent: 55, color: "#f59e0b", text: "Fair", textColor: "#f59e0b" };
        if (metCount <= 4) return { percent: 80, color: "#3b82f6", text: "Good", textColor: "#3b82f6" };
        return { percent: 100, color: "#10b981", text: "Strong", textColor: "#10b981" };
    }, [requirements]);

    const handleClose = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setFormData({ UserName: '', Email: '', Password: '', ConfirmPassword: '', SuperAdminPassword: '' });
        setErrors({});
        setMessage('');
        setSelectedDb({ value: '', label: 'Current Database (Default)' });
        setSelectedRoles(['Admin']);
        setShowSuperAdminWarning(false);
        setActiveTab('account');
        setRolePermissions([]);
        setShowPassword(false);
        setShowConfirmPassword(false);
        setShowSuperPassword(false);
        if (onClose) onClose();
    };

    const handleRoleChange = (roleName) => {
        if (roleName === 'Admin') return; // Admin is required

        if (roleName === 'Super Admin' && !selectedRoles.includes('Super Admin')) {
            setShowSuperAdminWarning(true);
            return;
        }

        setSelectedRoles(prev => {
            if (prev.includes(roleName)) return prev.filter(r => r !== roleName);
            return [...prev, roleName];
        });
    };

    const confirmSuperAdminRole = () => {
        setSelectedRoles(prev => [...prev, 'Super Admin']);
        setRolePermissions([]);
        setShowSuperAdminWarning(false);
        setActiveTab('account');
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setMessage('');
        setErrors({});

        const newErrors = {};
        if (!formData.UserName) newErrors.UserName = 'UserName is required.';
        else if (formData.UserName.length < 6) newErrors.UserName = 'UserName must be at least 6 characters.';
        else if (!/^[a-zA-Z0-9]+$/.test(formData.UserName)) newErrors.UserName = 'UserName can only contain letters and numbers.';

        if (!formData.Email) newErrors.Email = 'Email is required.';
        else if (!/\S+@\S+\.\S+/.test(formData.Email)) newErrors.Email = 'Invalid email format.';
        
        if (!formData.Password) newErrors.Password = 'Password is required.';
        else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/.test(formData.Password)) {
            newErrors.Password = 'Password must have at least 6 characters, 1 lowercase, 1 uppercase, 1 number and 1 special character.';
        }
        
        if (!formData.ConfirmPassword) newErrors.ConfirmPassword = 'Confirm Password is required.';
        else if (formData.Password !== formData.ConfirmPassword) newErrors.ConfirmPassword = 'Passwords do not match.';
        
        if (!formData.SuperAdminPassword) newErrors.SuperAdminPassword = 'Super Admin Password is required.';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        if (selectedRoles.length === 0) {
            setErrors(prev => ({ ...prev, Roles: 'Please select at least one role.' }));
            return;
        }

        if (!selectedRoles.includes('Super Admin')) {
            setShowPermissionsConfirm(true);
            return;
        }

        executeSubmit();
    };

    const executeSubmit = async () => {
        setShowPermissionsConfirm(false);
        setLoading(true);
        try {
            const payload = { ...formData, TargetDatabase: selectedDb?.value, Roles: selectedRoles, Permissions: rolePermissions };
            const res = await UserRoleAPI.createAdmin(payload);
            if (res.data?.Succeeded || res.data?.succeeded) {
                setMessageType('success');
                setMessage(res.data?.Message || res.data?.message || 'Admin account created successfully!');
                setFormData({ UserName: '', Email: '', Password: '', ConfirmPassword: '', SuperAdminPassword: '' });
                timeoutRef.current = setTimeout(() => {
                    handleClose();
                }, 5000);
            } else {
                setMessageType('danger');
                setMessage(res.data?.Message || res.data?.message || res.data?.ErrorMessage || res.data?.errorMessage || 'Failed to create admin account.');
            }
        } catch (error) {
            setMessageType('danger');
            setMessage(error.response?.data?.Message || error.response?.data?.message || error.response?.data?.ErrorMessage || error.response?.data?.errorMessage || error.message);
        }
        setLoading(false);
    };

    return (
        <React.Fragment>
        <CenteredModal
            show={show}
            onClose={handleClose}
            title="Create Admin Account"
            confirmText="Create Account"
            size="lg"
            modalType="modal-lg"
            formId="formCreateAdmin"
            isSubmitting={loading}
            submittingText="Creating..."
                        blurred={false}
        >
            <div className="px-3 pb-0">
                <p className="text-muted fw-bold fs-9 mb-3 mt-2">
                    Create a new administrator account with Super Admin privileges.
                </p>

                <div className="mb-3 border-bottom d-flex gap-4 px-2 nav" role="tablist">
                    <button 
                        className={`btn btn-link text-decoration-none fs-9 fw-bold px-2 pb-2 rounded-0 nav-link custom-tab-btn ${activeTab === 'account' ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); setActiveTab('account'); }}
                        type="button" 
                        role="tab" 
                        style={{ marginBottom: '-1px' }}
                    >
                        Account Details
                    </button>
                    <button 
                        className={`btn btn-link text-decoration-none fs-9 fw-bold px-2 pb-2 rounded-0 nav-link custom-tab-btn ${activeTab === 'permissions' ? 'active' : ''} ${selectedRoles.includes('Super Admin') ? 'disabled opacity-50' : ''}`}
                        onClick={(e) => { 
                            e.preventDefault(); 
                            if (!selectedRoles.includes('Super Admin')) {
                                setActiveTab('permissions');
                            }
                        }}
                        type="button" 
                        role="tab" 
                        disabled={selectedRoles.includes('Super Admin')}
                        style={{ marginBottom: '-1px', cursor: selectedRoles.includes('Super Admin') ? 'not-allowed' : 'pointer' }}
                    >
                        Assign Permissions
                    </button>
                </div>

                {message && (
                    <div className={`auth-${messageType === 'danger' ? 'error' : messageType}-message mb-4`}>
                        {messageType === 'danger' && <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" />}
                        <span>{message}</span>
                    </div>
                )}

                <form id="formCreateAdmin" onSubmit={handleSubmit}>
                {activeTab === 'account' && (
                    <>
                <div className="mb-4">
                    <label className="d-block fw-bold mb-1" style={{ fontSize: '0.875rem', color: '#16191f' }}>Target Database</label>
                    <Select
                        value={selectedDb}
                        onChange={setSelectedDb}
                        options={databases}
                        placeholder="Select Target Database..."
                        isClearable={false}
                        isDisabled={loading}
                        styles={{
                            control: (base, state) => ({
                                ...base,
                                fontSize: '0.875rem',
                                borderColor: state.isFocused ? '#2f5aa2' : base.borderColor,
                                boxShadow: state.isFocused ? '0 0 0 1px #2f5aa2' : base.boxShadow,
                                '&:hover': {
                                    borderColor: state.isFocused ? '#2f5aa2' : base.borderColor
                                }
                            }),
                            option: (base, state) => ({
                                ...base,
                                fontSize: '0.875rem',
                                backgroundColor: state.isSelected 
                                    ? '#2f5aa2' 
                                    : state.isFocused 
                                        ? '#2f5aa220' 
                                        : base.backgroundColor,
                                color: state.isSelected ? 'white' : base.color,
                                '&:active': {
                                    backgroundColor: '#2f5aa2'
                                }
                            }),
                            singleValue: (base) => ({
                                ...base,
                                fontSize: '0.875rem',
                            }),
                            placeholder: (base) => ({
                                ...base,
                                fontSize: '0.875rem',
                            })
                        }}
                    />
                </div>

                <div style={{ border: '1px solid #d5dbdb', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
                    <div style={{ background: '#f2f3f3', borderBottom: '1px solid #d5dbdb', padding: '12px 20px' }}>
                        <h6 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#16191f' }}>Account Details</h6>
                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#687078', marginTop: '2px' }}>Configure the basic information for the new admin.</p>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div className="row g-4 mb-4">
                            <div className="col-md-6">
                                <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                    UserName <span className="text-danger">*</span>
                                </label>
                                <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                    The unique identifier for logging in.
                                </p>
                                <input
                                    type="text"
                                    name="UserName"
                                    className={`form-control ${errors.UserName ? 'is-invalid' : ''}`}
                                    placeholder="Enter username"
                                    value={formData.UserName}
                                    onChange={handleChange}
                                    disabled={loading}
                                    autoComplete="off"
                                />
                                {errors.UserName && <div className="text-danger fw-bold fs-9 mt-1">{errors.UserName}</div>}
                            </div>
                            <div className="col-md-6">
                                <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                    Email <span className="text-danger">*</span>
                                </label>
                                <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                    The contact email address for this admin.
                                </p>
                                <input
                                    type="email"
                                    name="Email"
                                    className={`form-control ${errors.Email ? 'is-invalid' : ''}`}
                                    placeholder="Enter email address"
                                    value={formData.Email}
                                    onChange={handleChange}
                                    disabled={loading}
                                    autoComplete="off"
                                />
                                {errors.Email && <div className="text-danger fw-bold fs-9 mt-1">{errors.Email}</div>}
                            </div>
                        </div>

                        <div className="row g-4 mb-4">
                            <div className="col-md-6">
                                <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                    New Admin Password <span className="text-danger">*</span>
                                </label>
                                <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                    Set the password for this new account.
                                </p>
                                <div className="position-relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="Password"
                                        className={`form-control ${errors.Password ? 'is-invalid' : ''}`}
                                        style={{ paddingRight: '40px' }}
                                        placeholder="Enter new password"
                                        value={formData.Password}
                                        onChange={handleChange}
                                        disabled={loading}
                                        autoComplete="new-password"
                                    />
                                    <FontAwesomeIcon 
                                        icon={showPassword ? faEyeSlash : faEye} 
                                        className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary" 
                                        style={{ cursor: 'pointer', zIndex: 10 }}
                                        onClick={() => setShowPassword(!showPassword)} 
                                    />
                                </div>
                                {errors.Password && <div className="text-danger fw-bold fs-9 mt-1">{errors.Password}</div>}
                                {formData.Password.length > 0 && (
                                    <div className="mt-2 px-1">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="fs-10 fw-bold text-body-tertiary">Password Strength</span>
                                            <span className="fs-10 fw-bold" style={{ color: strength.textColor }}>
                                                {strength.text}
                                            </span>
                                        </div>
                                        <div className="progress" style={{ height: "4px" }}>
                                            <div
                                                className="progress-bar"
                                                role="progressbar"
                                                style={{ width: `${strength.percent}%`, backgroundColor: strength.color, transition: 'width 0.3s ease, background-color 0.3s ease' }}
                                                aria-valuenow={strength.percent}
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                            ></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="col-md-6">
                                <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                    Confirm New Password <span className="text-danger">*</span>
                                </label>
                                <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                    Re-enter the password to confirm.
                                </p>
                                <div className="position-relative">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="ConfirmPassword"
                                        className={`form-control ${errors.ConfirmPassword ? 'is-invalid' : ''}`}
                                        style={{ paddingRight: '40px' }}
                                        placeholder="Confirm new password"
                                        value={formData.ConfirmPassword}
                                        onChange={handleChange}
                                        disabled={loading}
                                        autoComplete="new-password"
                                    />
                                    <FontAwesomeIcon 
                                        icon={showConfirmPassword ? faEyeSlash : faEye} 
                                        className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary" 
                                        style={{ cursor: 'pointer', zIndex: 10 }}
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                                    />
                                </div>
                                {errors.ConfirmPassword && <div className="text-danger fw-bold fs-9 mt-1">{errors.ConfirmPassword}</div>}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                Assign Roles <span className="text-danger">*</span>
                            </label>
                            <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                Select the roles to assign to this new admin.
                            </p>
                            <div className="d-flex flex-wrap gap-3 mt-2">
                                {availableRoles.map(role => (
                                    <div className="form-check form-switch" key={role.Id}>
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id={`role-${role.Id}`}
                                            checked={selectedRoles.includes(role.Name)}
                                            onChange={() => handleRoleChange(role.Name)}
                                            disabled={loading || role.Name === 'Admin'}
                                            style={{ cursor: role.Name === 'Admin' ? 'not-allowed' : 'pointer' }}
                                        />
                                        <label className="form-check-label fw-bold fs-9" htmlFor={`role-${role.Id}`} style={{ cursor: role.Name === 'Admin' ? 'not-allowed' : 'pointer', color: '#16191f' }}>
                                            {role.Name}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            {errors.Roles && <div className="text-danger fw-bold fs-9 mt-1">{errors.Roles}</div>}
                        </div>

                        <div>
                            <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                Super Admin Password (To Confirm) <span className="text-danger">*</span>
                            </label>
                            <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                System Super Admin password required to authorize this action.
                            </p>
                            <div className="position-relative">
                                <input
                                    type={showSuperPassword ? "text" : "password"}
                                    name="SuperAdminPassword"
                                    className={`form-control ${errors.SuperAdminPassword ? 'is-invalid' : ''}`}
                                    style={{ paddingRight: '40px' }}
                                    placeholder="Enter system Super Admin password"
                                    value={formData.SuperAdminPassword}
                                    onChange={handleChange}
                                    disabled={loading}
                                    autoComplete="new-password"
                                />
                                <FontAwesomeIcon 
                                    icon={showSuperPassword ? faEyeSlash : faEye} 
                                    className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary" 
                                    style={{ cursor: 'pointer', zIndex: 10 }}
                                    onClick={() => setShowSuperPassword(!showSuperPassword)} 
                                />
                            </div>
                            {errors.SuperAdminPassword && <div className="text-danger fw-bold fs-9 mt-1">{errors.SuperAdminPassword}</div>}
                        </div>
                    </div>
                </div>
                    </>
                )}

                {activeTab === 'permissions' && (
                    <div className="mb-4 border rounded p-3 bg-white" style={{ minHeight: '300px', maxHeight: '450px', overflowY: 'auto', overflowX: 'hidden' }}>
                        <PermissionTab 
                            allPermissions={allPermissions}
                            rolePermissions={rolePermissions}
                            setRolePermissions={setRolePermissions}
                            disabled={loading || selectedRoles.includes('Super Admin')}
                        />
                    </div>
                )}
                </form>
            </div>
        </CenteredModal>

        {/* Warning Modal */}
        <CenteredModal 
            show={showSuperAdminWarning}
            onClose={() => setShowSuperAdminWarning(false)}
            onConfirm={confirmSuperAdminRole}
            title="CRITICAL SECURITY WARNING"
            confirmText="Yes, Grant Super Admin"
            closeText="Cancel"
            size="md"
            zIndex={1100}
        >
            <div className="rounded-3 border p-4 text-center" style={{ background: "#f8faff", borderColor: "#dfe8f7" }}>
                <div
                    className="d-inline-flex justify-content-center align-items-center rounded-circle mb-3"
                    style={{ width: "64px", height: "64px", backgroundColor: "#ffebee", color: "#d32f2f" }}
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                </div>
                
                <h4 className="fw-bold mb-3" style={{ color: "#d32f2f" }}>High Risk Action!</h4>
                <p className="fs-6 text-dark fw-bold mb-3">
                    You are about to grant <span style={{ color: "#d32f2f" }}>Super Admin</span> privileges.
                </p>
                <p className="text-muted fs-9 mb-0" style={{ lineHeight: "1.6" }}>
                    This user will have <strong>unrestricted access</strong> to the entire application. This includes full control over <strong>financial management, billing settings, sensitive user data</strong>, and core security configurations. Please ensure you absolutely trust this user with full system control.
                </p>
            </div>
        </CenteredModal>
                {/* Permissions Confirm Modal */}
        <CenteredModal 
            show={showPermissionsConfirm}
            onClose={() => setShowPermissionsConfirm(false)}
            onConfirm={executeSubmit}
            title="Confirm Assigned Permissions"
            confirmText="Yes, Create Account"
            closeText="Cancel"
            size="lg"
            modalType="modal-lg"
            zIndex={1100}
            blurred={false}
            isSubmitting={loading}
            submittingText="Creating..."
        >
            <div className="p-2">
                <div className="p-3 mb-3 rounded d-flex align-items-start" style={{ backgroundColor: '#f0f4f8', borderLeft: '4px solid #2f5aa2' }}>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-3 mt-1" style={{ color: '#2f5aa2', fontSize: '1.25rem' }} />
                    <div>
                        <h6 className="fw-bold mb-1" style={{ color: '#2f5aa2', fontSize: '0.9rem' }}>Review Assigned Permissions</h6>
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>Please double-check the permissions you have assigned to this admin account before proceeding.</span>
                    </div>
                </div>
                
                {rolePermissions.length === 0 ? (
                    <div className="text-center text-muted py-4 border rounded bg-light">
                        <FontAwesomeIcon icon={faExclamationCircle} className="fs-1 mb-2 text-warning" />
                        <h6 className="fw-bold text-dark">No Permissions Assigned</h6>
                        <p className="mb-0 fs-9">This user will only have the default Admin role with no specific screen accesses.</p>
                    </div>
                ) : (
                    <div className="border rounded p-3 bg-light" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <h6 className="fw-bold mb-3 border-bottom pb-2">Assigned Permissions ({rolePermissions.length})</h6>
                        {(() => {
                            const tree = {};
                            rolePermissions.forEach(perm => {
                                const parts = perm.split('.');
                                const action = parts.pop();
                                const modulePath = parts.join('.');
                                if (!tree[modulePath]) tree[modulePath] = [];
                                tree[modulePath].push({ id: perm, action });
                            });
                            
                            const rootKeys = Object.keys(tree);
                            const half = Math.ceil(rootKeys.length / 2);
                            const leftKeys = rootKeys.slice(0, half);
                            const rightKeys = rootKeys.slice(half);

                            const renderColumn = (keys) => (
                                <div className="col-md-6">
                                    {keys.map((modulePath, idx) => {
                                        const moduleName = modulePath.replace(/([A-Z])/g, ' $1').trim().replace(/\./g, ' > ');
                                        const actions = tree[modulePath];
                                        const accessPerm = actions.find(a => a.action === 'Access');
                                        const otherActions = actions.filter(a => a.action !== 'Access');
                                        
                                        return (
                                            <div key={modulePath} className={`tree-group ${idx !== keys.length - 1 ? 'mb-3 pb-3 border-bottom' : 'mb-3'}`}>
                                                <div className="fw-bold mb-2" style={{ color: '#2f5aa2', fontSize: '0.9rem' }}>
                                                    {moduleName}
                                                </div>
                                                <div className="ms-3">
                                                    {accessPerm && (
                                                        <div className="mb-2 pb-2" style={{ borderBottom: otherActions.length > 0 ? '1px dashed #e8eaed' : 'none' }}>
                                                            <span className="fs-9 fw-bold text-primary">
                                                                Enable Screen Access (Show in Menu)
                                                            </span>
                                                        </div>
                                                    )}
                                                    {otherActions.length > 0 && (
                                                        <div className="d-flex flex-column gap-1 ms-1">
                                                            <div className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>AVAILABLE ACTIONS</div>
                                                            {otherActions.map(p => (
                                                                <div key={p.id} className="m-0">
                                                                    <span className="fs-9 text-body-emphasis">
                                                                        • {p.action.replace(/([A-Z])/g, ' $1').trim()}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );

                            return (
                                <div className="row">
                                    {renderColumn(leftKeys)}
                                    {renderColumn(rightKeys)}
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>
        </CenteredModal>
        
        </React.Fragment>
    );
}

export default CreateAdminModal;
